import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, IconButton, Tooltip } from '@material-ui/core';
import { FileCopyOutlined, CheckCircleOutline } from '@material-ui/icons';

import { ReservationDetails } from '../../types';
import { SimpleCard } from '../common';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { getApaleoReservationUrl } from '../../util';

type Props = Pick<ReservationDetails, 'reservation'>;

const renderReservationRow = (label: string, propertyId: string, reservationId: string) => {
  const { t } = useTranslation();
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  const isIdCopied = isCopied(reservationId);
  const reservationUrl = getApaleoReservationUrl(propertyId, reservationId);

  const handleCopyReservationLink = (reservationId: string) => {
    copyToClipboard(reservationUrl, reservationId);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
      <Typography component="span">
        <strong>{label}: </strong>
        <a
          href={reservationUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#1976d2',
            textDecoration: 'underline',
          }}
        >
          {reservationId}
        </a>
      </Typography>
      <Tooltip title={isIdCopied ? t('copied') : t('copyLink')}>
        <IconButton
          size="small"
          onClick={() => handleCopyReservationLink(reservationId)}
          style={{
            padding: '4px',
            color: isIdCopied ? '#4caf50' : 'inherit',
          }}
        >
          {isIdCopied ? <CheckCircleOutline fontSize="small" /> : <FileCopyOutlined fontSize="small" />}
        </IconButton>
      </Tooltip>
    </div>
  );
};

function ExtendedReservation({ reservation }: Props) {
  const { t } = useTranslation();
  const location = useLocation();

  if (!reservation) {
    return null;
  }

  const { firstReservationId, previousReservationId, nextReservationId } = reservation;

  if (!firstReservationId && !previousReservationId && !nextReservationId) {
    return null;
  }

  const params = new URLSearchParams(location.search);
  const propertyId = params.get('propertyId') || '';

  return (
    <SimpleCard title={t('sections.reservationExtension')}>
      {firstReservationId &&
        renderReservationRow(t('reservationExtension.firstReservation'), propertyId, firstReservationId)}
      {previousReservationId &&
        previousReservationId !== firstReservationId &&
        renderReservationRow(t('reservationExtension.previous'), propertyId, previousReservationId)}
      {nextReservationId && renderReservationRow(t('reservationExtension.next'), propertyId, nextReservationId)}
    </SimpleCard>
  );
}

export default ExtendedReservation;
